import { dxtToLocalServerTime, useContextMenuMESF } from "@dexteel/mesf-core";
import { Box, Skeleton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Asset, Ganttv2, Task, ViewMode } from "@/controls/ganttAsset";
import {
	GanttData,
	GanttEvent,
} from "@/pages/home/components/gantt-section/models/types";
import { getGanttData } from "@/pages/home/components/gantt-section/repositories/ganttRepository";
import {
	createProcessLine,
	createReclassifiedTask,
	createWorkOrderTask,
	withDelayLines,
} from "@/pages/home/components/gantt-section/taskBuilders";
import {
	GANTT_DELAYS_MENU_ID,
	useGanttOptionsContextMenu,
} from "@/pages/home/hooks/useGanttOptionsContextMenu";
import {
	AREA_COLOR_SAWS,
	CUTBACK_COLOR,
	CUTBACK_COLOR_LIGHT,
	CUTBACK_COLOR_LIGHTER,
} from "@/shared/colors";
import {
	GANTT_WO_MENU_ID,
	useWorkOrderGanttMenu,
} from "@/pages/workOrders/hooks/useWorkOrderGanttMenu";
import { SawBundleTooltip } from "./SawBundleTooltip";

// Lane label shown in the gantt's left gutter.
const SAW_LANE_NAME = "20th Street Saw";
// SAW-teal shades for the task bars (the lane accent is AREA_COLOR_SAWS).
const SAW_BAR_BG = "#9fc6d0";
const SAW_BAR_BG_SELECTED = "#cfe4ea";

interface Props {
	shiftStart: Date | null;
	shiftEnd: Date | null;
	assetId?: number | null;
	loading?: boolean;
}

const pickViewMode = (start: Date, end: Date): ViewMode => {
	const hours = (end.getTime() - start.getTime()) / 3_600_000;
	if (hours <= 12) return ViewMode.Hour;
	if (hours <= 24) return ViewMode.TwoHour;
	if (hours <= 72) return ViewMode.HalfDay;
	return ViewMode.Day;
};

const tickCount = (start: Date, end: Date, vm: ViewMode): number => {
	const hours = (end.getTime() - start.getTime()) / 3_600_000;
	switch (vm) {
		case ViewMode.Hour:
			return Math.ceil(hours);
		case ViewMode.TwoHour:
			return Math.ceil(hours / 2);
		case ViewMode.HalfDay:
			return Math.ceil(hours / 12);
		default:
			return Math.ceil(hours / 24);
	}
};

// "Bundles" view = one bar per cut (~7 min); "Batches" (not fetched) = mill bundles.
const createSawTask = (event: GanttEvent): Task => {
	const isBatch = event.EventType === "Batch";
	const label = isBatch ? "Batch" : "Bundle";
	const id = isBatch ? event.BatchId : event.BundleId;
	const isCutback = event.IsCutback === true;
	return {
		id: `${event.EventType.toLowerCase()}-${id}`,
		start: event.Start,
		end: event.End,
		processStart: event.Start,
		type: "task",
		progress: 100,
		name: `${label} ${id}`,
		displayName: isCutback ? "Cutback" : `${event.BarCount ?? ""}`,
		processLines: [],
		source: event,
		styles: isCutback
			? {
					backgroundColor: CUTBACK_COLOR_LIGHT,
					backgroundSelectedColor: CUTBACK_COLOR_LIGHTER,
					progressColor: CUTBACK_COLOR,
					borderColor: CUTBACK_COLOR,
				}
			: {
					backgroundColor: SAW_BAR_BG,
					backgroundSelectedColor: SAW_BAR_BG_SELECTED,
					progressColor: AREA_COLOR_SAWS,
					borderColor: AREA_COLOR_SAWS,
				},
		textLeftTop: event.Start ? dxtToLocalServerTime(event.Start, "HH:mm") : "",
		textRightTop: "",
		textLeftBottom: "",
		textRightBottom: event.End ? dxtToLocalServerTime(event.End, "HH:mm") : "",
	};
};

// Renders the East Saw production timeline using the same Ganttv2 control as the
// weld overview, fed by FAB.GetGanttData scoped to the saw asset (@AssetId).
// Falls back to an empty teal lane when the asset/shift hasn't resolved yet.
export const SawShiftGantt = ({
	shiftStart,
	shiftEnd,
	assetId,
	loading,
}: Props) => {
	const [width, setWidth] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getOptions: getWoMenuOptions, menu: woMenu } =
		useWorkOrderGanttMenu();
	const { getOptions: getDelayMenuOptions, menu: delayMenu } =
		useGanttOptionsContextMenu(["sawGanttData"], "Saw Gantt");

	useEffect(() => {
		registerConfig({
			id: GANTT_DELAYS_MENU_ID,
			getOptions: getDelayMenuOptions,
		});
		registerConfig({
			id: GANTT_WO_MENU_ID,
			getOptions: getWoMenuOptions,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!containerRef.current) return;
		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) setWidth(entry.contentRect.width);
		});
		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, []);

	// Always show the lane: fall back to a default day window if the shift hasn't
	// resolved yet, so the teal band never disappears.
	const [start, end] = useMemo<[Date, Date]>(() => {
		if (shiftStart && shiftEnd && shiftEnd > shiftStart) {
			return [shiftStart, shiftEnd];
		}
		const s = new Date();
		s.setHours(6, 0, 0, 0);
		const e = new Date();
		e.setHours(18, 0, 0, 0);
		return [s, e];
	}, [shiftStart, shiftEnd]);

	const { data, isLoading } = useQuery<GanttData, Error>({
		queryKey: ["sawGanttData", assetId, start.toISOString(), end.toISOString()],
		queryFn: () => getGanttData(start, end, "Bundles", assetId),
		enabled: !!assetId,
		refetchInterval: 60000,
		meta: { realtime: ["FAB.GetGanttData"] },
	});

	const viewMode = useMemo(() => pickViewMode(start, end), [start, end]);

	const reclassifiedCount =
		data?.events?.filter((e) => e.IsReclassified === true).length ?? 0;
	const hasBottomBand =
		(data?.workOrders?.length ?? 0) > 0 || reclassifiedCount > 0;

	const assets = useMemo<Asset[]>(
		() => [
			{
				id: "SawEast",
				name: SAW_LANE_NAME,
				color: AREA_COLOR_SAWS,
				processes: [
					...withDelayLines(
						(data?.events ?? []).map(createSawTask),
						(data?.delays ?? []).map(createProcessLine),
						start,
						end,
					),
					...(data?.workOrders ?? []).map(createWorkOrderTask),
					...(data?.events ?? [])
						.filter((e) => e.IsReclassified === true)
						.map(createReclassifiedTask),
				],
				hasBottomLane:
					(data?.workOrders?.length ?? 0) > 0 ||
					(data?.events?.some((e) => e.IsReclassified === true) ?? false),
			},
		],
		[data, start, end],
	);

	const columnWidth = useMemo(() => {
		if (width === 0) return 100;
		const ticks = Math.max(tickCount(start, end, viewMode), 1);
		const available = width - 160 - 40; // assetCellWidth + padding
		return Math.max(Math.floor(available / ticks), 60);
	}, [width, start, end, viewMode]);

	// With a bottom band (WOs / reclassified segments) the row splits like the
	// Home gantt; without it keep the slim strip. The calendar is trimmed to
	// give the row the height instead.
	const rowHeight = hasBottomBand ? 120 : 84;
	const headerHeight = 40;

	const handleContextMenu = (event: React.MouseEvent, source: any) => {
		if (source?.DelayId) {
			showContextMenu(event, source, GANTT_DELAYS_MENU_ID);
			return;
		}
		if (source?.WorkOrderId != null && source?.EventType == null) {
			event.preventDefault();
			showContextMenu(event, source, GANTT_WO_MENU_ID);
		}
	};

	return (
		<Box ref={containerRef} sx={{ width: "100%" }}>
			{loading || isLoading ? (
				<Skeleton variant="rounded" width="100%" height={rowHeight} />
			) : (
				<Ganttv2
					assets={assets}
					viewMode={viewMode}
					startCalendarDate={start}
					endCalendarDate={end}
					columnWidth={columnWidth}
					rowHeight={rowHeight}
					headerHeight={headerHeight}
					barFill={hasBottomBand ? 45 : 70}
					lineSectionFill={hasBottomBand ? 20 : 30}
					lineFill={80}
					showArrowTriangle={false}
					TooltipContent={SawBundleTooltip}
					onContextMenu={handleContextMenu}
				/>
			)}
			{delayMenu}
			{woMenu}
		</Box>
	);
};
